import { apiGet, apiPost } from '@/api/client';

export interface SearchResult {
  id: number;
  type: string;
  title: string;
  description?: string;
  url?: string;
}

export interface SearchSuggestion {
  text: string;
  type: string;
}

/** A single hit from the V3 global search (`GET /api/search`), shaped by the backend. */
export interface GlobalSearchHit {
  id: string;
  type: 'report' | 'building' | 'request' | 'vault';
  title: string;
  description: string;
  link: string;
}

/** A page of V3 global-search results (`items` + total/page/limit). */
export interface GlobalSearchPage {
  items: GlobalSearchHit[];
  total: number;
  page: number;
  limit: number;
  /** Lowercase words the server matched on, for highlighting (FR-517). */
  terms?: string[];
}

/** Entity buckets the V3 global search can scope to (`@dbtable` in the legacy proc). */
export type GlobalSearchCategory = 'all' | 'reports' | 'buildings' | 'requests' | 'vault';

export interface GlobalSearchParams {
  q: string;
  category?: GlobalSearchCategory;
  sort?: 'title' | 'description';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export const searchApi = {
  /**
   * V3 global search across reports, buildings, and work orders. Results are scoped server-side to
   * the caller's portfolio (SSI = all, company admin = owned, others = selected). Port of the legacy
   * `ssi_GlobalSearch` proc; replaces the Azure/legacy `POST /search/GlobalSearch` call.
   */
  search(params: GlobalSearchParams): Promise<GlobalSearchPage> {
    return apiGet<GlobalSearchPage>('/search', { ...params });
  },

  /** Legacy global search (envelope/proc bridge). Retained for reference; superseded by `search`. */
  globalSearch(userId: number, searchText: string, category?: string): Promise<SearchResult[]> {
    return apiPost<SearchResult[]>('/search/GlobalSearch', { searchText, category, userId });
  },

  /** Azure-powered global search */
  azureSearch(userId: number, searchTerm: string): Promise<SearchResult[]> {
    return apiGet<SearchResult[]>(`/search/AzureGlobalSearch/${userId}/${encodeURIComponent(searchTerm)}`);
  },

  /** Auto-suggest / search suggestions */
  getSuggestions(userId: number, searchTerm: string): Promise<SearchSuggestion[]> {
    return apiGet<SearchSuggestion[]>(`/search/LoadSuggestion/${userId}/${encodeURIComponent(searchTerm)}`);
  },

  /** Search buildings */
  searchBuildings(params: {
    searchText: string;
    userId: number;
    take: number;
    skip: number;
    filter?: string;
  }): Promise<unknown> {
    return apiPost<unknown>('/search/search-buildings', params);
  },

  /** Search by building code */
  searchBuildingCode(searchText: string, isGetAll = false): Promise<unknown[]> {
    return apiGet<unknown[]>(`/search/search-building-code/${encodeURIComponent(searchText)}`, { isGetAll });
  },
};
